import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@vseh/ui";

/**
 * Прозрачно показывает стоимость AI-сессии. По §14 «Cost meter» —
 * всегда видна, не выпячивается. Клик раскрывает breakdown.
 */
export interface CostMeterEntry {
  label: string;            // "gpt-4o", "claude-sonnet-4-6", агент или модель
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface CostMeterProps {
  entries: CostMeterEntry[];
  className?: string;
  /** Курс для отображения в ₽ помимо $. Если undefined — только $. */
  rubPerUsd?: number;
}

function formatUsd(value: number): string {
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;
}

function formatRub(value: number): string {
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(value);
}

export function CostMeter({ entries, rubPerUsd, className }: CostMeterProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const totalCalls    = entries.reduce((s, e) => s + e.calls, 0);
  const totalTokens   = entries.reduce((s, e) => s + e.inputTokens + e.outputTokens, 0);
  const totalCostUsd  = entries.reduce((s, e) => s + e.costUsd, 0);
  const totalCostRub  = rubPerUsd ? Math.round(totalCostUsd * rubPerUsd) : null;

  return (
    <div className={cn("inline-flex flex-col items-end text-2xs text-base-500", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 hover:bg-base-100"
      >
        <span className="font-mono tabular-nums text-base-700">
          {formatUsd(totalCostUsd)}
          {totalCostRub != null && <span className="text-base-500"> · {formatRub(totalCostRub)} ₽</span>}
        </span>
        <span aria-hidden="true">·</span>
        <span className="tabular-nums">{totalTokens.toLocaleString("ru-RU")} токенов</span>
        <span aria-hidden="true">·</span>
        <span className="tabular-nums">{totalCalls} вызовов</span>
        {open ? (
          <ChevronUp aria-hidden="true" className="size-3" />
        ) : (
          <ChevronDown aria-hidden="true" className="size-3" />
        )}
      </button>

      {open && (
        <div className="mt-1 w-72 rounded-md border border-base-200 bg-surface p-3 shadow-md">
          <div className="mb-2 text-2xs font-medium text-base-700">Разбивка по моделям</div>
          <table className="w-full text-2xs">
            <thead>
              <tr className="text-base-500">
                <th scope="col" className="text-left font-normal">Модель</th>
                <th scope="col" className="text-right font-normal">Вызовы</th>
                <th scope="col" className="text-right font-normal">Токены</th>
                <th scope="col" className="text-right font-normal">$</th>
              </tr>
            </thead>
            <tbody className="text-base-700">
              {entries.map((e) => (
                <tr key={e.label} className="border-t border-base-100">
                  <td className="py-1 font-mono">{e.label}</td>
                  <td className="py-1 text-right tabular-nums">{e.calls}</td>
                  <td className="py-1 text-right tabular-nums">
                    {(e.inputTokens + e.outputTokens).toLocaleString("ru-RU")}
                  </td>
                  <td className="py-1 text-right tabular-nums">{formatUsd(e.costUsd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
